import React, { useState, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  AlertCircle, ArrowRight, ArrowLeft, Heart, TrendingUp, Users,
  Sparkles, ShieldCheck, Globe, Award, Gift, ChevronDown
} from 'lucide-react';

const formatINR = (n) =>
  '₹' + Math.round(n).toLocaleString('en-IN');

const RegisterAngel = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    mobile: '',
    password: '',
    confirmPassword: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  const formRef = useRef(null);
  const scrollToForm = () =>
    formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });

  // Earnings calculator state
  const [referrals, setReferrals] = useState(20);
  const [avgCart, setAvgCart] = useState(2323);
  const monthlyEarning = referrals * avgCart * 0.05;

  const [openFaq, setOpenFaq] = useState(null);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    setLoading(true);
    const result = await register(
      formData.email,
      formData.password,
      formData.name,
      'angel',
      null,
      formData.mobile
    );

    if (result.success) {
      navigate('/angel-dashboard');
    } else {
      setError(result.error);
    }
    setLoading(false);
  };

  const faqs = [
    {
      q: 'Do I need to run a business to be a Kaur?',
      a: 'Not at all. Anyone who loves the brands on Nanaki Kaur and wants to share them can become a Kaur. Friends, students, homemakers, working professionals — everyone is welcome.'
    },
    {
      q: 'When and how do I get paid?',
      a: 'Earnings accumulate in your Kaur dashboard in real-time. Payouts are processed monthly via UPI/bank transfer once your balance crosses ₹500. No minimum target, no time limit.'
    },
    {
      q: 'Is there a target I must hit?',
      a: 'No targets, no pressure. Refer one friend or a hundred — your code keeps earning 5% on every order, lifetime, with no deactivation policy.'
    },
    {
      q: 'How does my friend use my code?',
      a: 'They simply enter your unique code (e.g. PRIYA5) at checkout. They instantly get 5% off the order, and you earn 5% commission on the same order — no math required from either side.'
    },
    {
      q: 'Are there any joining fees?',
      a: 'Zero. Becoming a Kaur is completely free, forever. No subscription, no inventory commitment, no hidden costs.'
    },
    {
      q: 'What about taxes on my earnings?',
      a: 'Commission income is treated as Other Income under Indian tax law. We provide an annual earnings statement so you can declare it correctly while filing your return. Reach out to a CA for advice on your specific bracket.'
    }
  ];

  return (
    <div className="min-h-screen bg-white" data-testid="register-angel-page">
      {/* Back link */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-6">
        <button
          onClick={() => navigate('/')}
          className="flex items-center text-purple-600 hover:text-purple-700 transition"
          data-testid="back-to-home-btn"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Home
        </button>
      </div>

      {/* HERO */}
      <section className="relative overflow-hidden bg-gradient-to-br from-purple-600 via-pink-600 to-rose-500 text-white">
        <div
          className="absolute inset-0 opacity-20"
          style={{
            backgroundImage:
              'radial-gradient(circle at 20% 20%, rgba(255,255,255,0.4) 0, transparent 40%), radial-gradient(circle at 80% 60%, rgba(255,255,255,0.3) 0, transparent 40%)'
          }}
        />
        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24 text-center">
          <span className="inline-block bg-white/20 backdrop-blur-sm rounded-full px-4 py-1 text-xs font-semibold uppercase tracking-wider mb-6">
            For women who love sharing what they love
          </span>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
            Earn 5% on every order<br className="hidden sm:block" />{' '}
            you refer.
          </h1>
          <p className="text-lg md:text-xl opacity-95 max-w-3xl mx-auto mb-10">
            Become a <strong>Nanaki&apos;s Kaur</strong> — share your unique code with friends and family,
            and earn lifetime commission on every order they place. They get 5% off, you earn 5%. Everyone wins.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              type="button"
              onClick={scrollToForm}
              className="bg-white text-purple-700 px-8 py-3 rounded-lg font-semibold hover:bg-purple-50 transition flex items-center justify-center"
              data-testid="hero-apply-btn"
            >
              Become a Kaur
              <ArrowRight className="w-5 h-5 ml-2" />
            </button>
            <a
              href="#how-it-works"
              className="bg-white/10 border border-white/40 backdrop-blur text-white px-8 py-3 rounded-lg font-semibold hover:bg-white/20 transition flex items-center justify-center"
            >
              How it works
            </a>
          </div>
          <p className="text-xs opacity-75 mt-6">
            ✨ Zero joining fee · No targets · Lifetime commission
          </p>
        </div>
      </section>

      {/* WHAT 'KAUR' MEANS */}
      <section className="py-20 md:py-28 bg-white" data-testid="kaur-meaning-section">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-5 gap-12 items-center">
            <div className="md:col-span-2 text-center md:text-left">
              <span className="inline-block text-xs uppercase tracking-[0.3em] text-[#B6594C] mb-4">
                What &lsquo;Kaur&rsquo; means
              </span>
              <p className="font-serif text-7xl md:text-8xl lg:text-9xl text-[#2A1B2D] leading-none mb-2" style={{ fontFamily: '"Cormorant Garamond", serif' }}>
                Kaur
              </p>
              <p className="font-serif text-xl text-[#B6594C] italic" style={{ fontFamily: '"Cormorant Garamond", serif' }}>
                /ko&#240;r/ &middot; <em>princess</em>
              </p>
            </div>

            <div className="md:col-span-3 space-y-4 text-stone-700 leading-relaxed">
              <p>
                In Sikhism, every woman is given the surname <strong>Kaur</strong> &mdash; meaning <em>princess</em>, <em>sovereign</em>,
                <em> lioness</em>. It was bequeathed in the late 17th century by Guru Gobind Singh Ji as a radical act of equality:
                no woman would ever again carry the caste of her father or husband. She belonged to herself.
              </p>
              <p>
                Kaur is sovereignty. It says: <em>you are not anyone&rsquo;s property; you are your own.</em> It is dignity stitched into a name.
              </p>
              <blockquote className="border-l-4 border-[#B6594C] pl-5 py-1 my-4">
                <p className="font-serif text-xl md:text-2xl italic text-[#2A1B2D] leading-snug" style={{ fontFamily: '"Cormorant Garamond", serif' }}>
                  &ldquo;Kaur&rdquo; turned every woman into royalty,<br className="hidden sm:block" />{' '}centuries before the world thought to.
                </p>
              </blockquote>
              <p>
                When you become a <strong>Nanaki&rsquo;s Kaur</strong>, you join a community honouring that legacy &mdash;
                where every woman recognises her own light, lifts the women around her, and earns with dignity.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* EARNINGS CALCULATOR */}
      <section className="py-16 md:py-20 bg-gradient-to-br from-purple-50 to-pink-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="text-3xl md:text-4xl font-bold mb-3">See what you could earn</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Drag the sliders. The maths updates instantly.
            </p>
          </div>

          <div className="bg-white rounded-2xl shadow-lg p-6 md:p-10">
            <div className="grid md:grid-cols-2 gap-8 mb-8">
              <div>
                <div className="flex items-center justify-between mb-3">
                  <label className="font-semibold text-gray-700">Monthly referrals</label>
                  <span className="text-purple-700 font-bold text-lg" data-testid="referrals-value">{referrals}</span>
                </div>
                <input
                  type="range"
                  min={1}
                  max={200}
                  step={1}
                  value={referrals}
                  onChange={(e) => setReferrals(Number(e.target.value))}
                  className="w-full accent-purple-600"
                  data-testid="referrals-slider"
                />
                <div className="flex justify-between text-xs text-gray-400 mt-1">
                  <span>1</span><span>50</span><span>100</span><span>200</span>
                </div>
              </div>
              <div>
                <div className="flex items-center justify-between mb-3">
                  <label className="font-semibold text-gray-700">Average order value</label>
                  <span className="text-purple-700 font-bold text-lg" data-testid="cart-value">{formatINR(avgCart)}</span>
                </div>
                <input
                  type="range"
                  min={500}
                  max={10000}
                  step={100}
                  value={avgCart}
                  onChange={(e) => setAvgCart(Number(e.target.value))}
                  className="w-full accent-purple-600"
                  data-testid="cart-slider"
                />
                <div className="flex justify-between text-xs text-gray-400 mt-1">
                  <span>₹500</span><span>₹3K</span><span>₹6K</span><span>₹10K</span>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-xl p-6 md:p-8 text-center">
              <p className="text-sm uppercase tracking-wider opacity-80 mb-1">Your projected monthly earning</p>
              <p className="text-4xl md:text-5xl font-bold mb-2" data-testid="projected-earning">
                {formatINR(monthlyEarning)}
              </p>
              <p className="text-sm opacity-90">
                {referrals} referrals × {formatINR(avgCart)} × 5% commission
              </p>
            </div>

            <p className="text-center text-xs text-gray-500 mt-4">
              And your friends save 5% on their order too — that&apos;s why they keep using your code.
            </p>
          </div>
        </div>
      </section>

      {/* TOP PERFORMER PRIZE — Amby Valley Villa Trip */}
      <section className="py-16 md:py-20 bg-[#2A1B2D] text-white relative overflow-hidden" data-testid="prize-section">
        <div className="absolute inset-0 opacity-[0.05] pointer-events-none" style={{
          backgroundImage: 'radial-gradient(#E8DCC8 1px, transparent 1px)',
          backgroundSize: '24px 24px'
        }} />
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="inline-block bg-[#B6594C]/20 text-[#E8DCC8] rounded-full px-4 py-1 text-xs font-semibold uppercase tracking-[0.3em] mb-6 border border-[#B6594C]/40">
              Top Performer Prize
            </span>
            <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-tight mb-6" style={{ fontFamily: '"Cormorant Garamond", serif' }}>
              Top earner each cycle wins<br className="hidden md:block" />{' '}
              a <em className="text-[#E8DCC8]">1-day getaway</em> in Amby Valley.
            </h2>
            <p className="text-white/70 max-w-2xl mx-auto leading-relaxed text-lg">
              The highest-earning Kaur of the cycle wins a luxury day-trip to a 4-bedroom Spanish villa in
              Amby Valley City, Lonavala — surrounded by nature, hills and curated activities.
            </p>
          </div>

          {/* Villa images */}
          <div className="grid md:grid-cols-2 gap-4 md:gap-6 mb-12">
            <div className="relative overflow-hidden rounded-2xl group">
              <img
                src="https://customer-assets.emergentagent.com/job_kaur-studio/artifacts/s2w7gfy2_1000037162.jpg"
                alt="Spanish Villa exterior at Amby Valley City — Nanaki Kaurs top performer prize"
                className="w-full h-72 md:h-96 object-cover group-hover:scale-105 transition duration-700"
                loading="lazy"
              />
            </div>
            <div className="relative overflow-hidden rounded-2xl group">
              <img
                src="https://customer-assets.emergentagent.com/job_kaur-studio/artifacts/nks6p1mm_1000037163.jpg"
                alt="Villa surrounded by palm trees and nature in Amby Valley, Lonavala"
                className="w-full h-72 md:h-96 object-cover group-hover:scale-105 transition duration-700"
                loading="lazy"
              />
            </div>
          </div>

          {/* What's included */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { t: '4-Bedroom Villa', d: 'Private Spanish-style villa to yourself for the day' },
              { t: 'Surrounded by Nature', d: 'Hills, gardens, palm trees and fresh Lonavala air' },
              { t: 'Curated Activities', d: 'Outdoor games, lakeside walks and adventure on-site' },
              { t: 'You + 3 Guests', d: 'Bring your closest people along for the day' }
            ].map((p) => (
              <div key={p.t} className="border border-white/15 rounded-xl p-5 backdrop-blur hover:bg-white/5 transition">
                <p className="font-serif text-2xl text-[#E8DCC8] mb-1" style={{ fontFamily: '"Cormorant Garamond", serif' }}>
                  {p.t}
                </p>
                <p className="text-sm text-white/70">{p.d}</p>
              </div>
            ))}
          </div>

          <p className="text-center text-xs text-white/50 mt-10 italic max-w-2xl mx-auto">
            Awarded to the highest-earning Nanaki Kaur of each cycle. Travel to/from Lonavala arranged separately. Cycle dates and terms shared with approved Kaurs in your dashboard.
          </p>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="how-it-works" className="py-16 md:py-20 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-3">How it works</h2>
          <p className="text-gray-600 text-center mb-12 max-w-2xl mx-auto">
            From sign-up to your first commission in under 5 minutes.
          </p>
          <div className="grid md:grid-cols-4 gap-8">
            {[
              { n: 1, t: 'Apply free', d: 'Sign up below with name, email, mobile and password. No paperwork, no fees.', i: <Sparkles className="w-6 h-6" /> },
              { n: 2, t: 'Get your code', d: 'On approval you get a unique referral code (e.g. PRIYA5) inside your Kaur dashboard.', i: <ShieldCheck className="w-6 h-6" /> },
              { n: 3, t: 'Share anywhere', d: 'WhatsApp it, post on Instagram, drop it in a coffee chat. Your code works on every order.', i: <Globe className="w-6 h-6" /> },
              { n: 4, t: 'Earn lifetime', d: '5% of every order tracked automatically. Withdraw via UPI when your balance hits ₹500.', i: <TrendingUp className="w-6 h-6" /> }
            ].map((s) => (
              <div key={s.n}>
                <div className="w-12 h-12 rounded-full bg-purple-100 text-purple-700 flex items-center justify-center mb-4">{s.i}</div>
                <span className="text-xs font-bold uppercase tracking-wider text-purple-600">Step {s.n}</span>
                <h3 className="font-bold text-lg mt-1 mb-2">{s.t}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHAT YOU GET */}
      <section className="py-16 md:py-20 bg-gradient-to-br from-pink-50 via-white to-purple-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-3">What you get as a Kaur</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              More than just commission — you become part of a community lifting other women up.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { i: <TrendingUp className="w-6 h-6" />, t: '5% lifetime commission', d: 'On every single order placed using your code, with no expiry.' },
              { i: <Gift className="w-6 h-6" />, t: '5% off for your friends', d: 'They get an instant discount the moment they enter your code.' },
              { i: <Award className="w-6 h-6" />, t: 'Real-time dashboard', d: 'Track referrals, earnings and payouts in your Kaur dashboard 24/7.' },
              { i: <Sparkles className="w-6 h-6" />, t: 'Early access to drops', d: 'Get first dibs on new product launches and exclusive Kaur-only previews.' },
              { i: <Heart className="w-6 h-6" />, t: 'Drishty Foundation impact', d: '23% of every Art Tee order funds vocational training for women.' },
              { i: <Users className="w-6 h-6" />, t: 'Featured Kaur spotlight', d: 'Top performers get featured across our website and Instagram.' }
            ].map((it) => (
              <div key={it.t} className="bg-white rounded-xl p-6 shadow-sm">
                <div className="w-10 h-10 rounded-lg bg-purple-100 text-purple-600 flex items-center justify-center mb-4">{it.i}</div>
                <h3 className="font-bold mb-2">{it.t}</h3>
                <p className="text-sm text-gray-600 leading-relaxed">{it.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SOCIAL PROOF */}
      <section className="py-16 md:py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">Kaurs in the wild</h2>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              {
                quote: 'I started sharing my code in our college WhatsApp group out of curiosity. ₹4,200 in my first month — and my friends keep thanking me for the discount!',
                name: 'Tanvi',
                city: 'Mumbai',
                emoji: '🎓'
              },
              {
                quote: 'Being a Kaur feels like a side hustle that aligns with my values. Every Art Tee I sell quietly funds Drishty Foundation. That hits different.',
                name: 'Riya',
                city: 'Bengaluru',
                emoji: '🌿'
              },
              {
                quote: 'I refer to skincare regulars at our salon. The dashboard makes me feel like a tiny CEO. The payout via UPI was smooth too.',
                name: 'Simran',
                city: 'Chandigarh',
                emoji: '💼'
              }
            ].map((q, i) => (
              <div key={i} className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-2xl p-6 border border-purple-100">
                <div className="text-4xl mb-3">{q.emoji}</div>
                <p className="text-gray-700 italic leading-relaxed mb-4">&ldquo;{q.quote}&rdquo;</p>
                <div className="font-semibold text-purple-700">{q.name}</div>
                <div className="text-xs text-gray-500">{q.city}</div>
              </div>
            ))}
          </div>
          <p className="text-xs text-gray-400 text-center mt-6 italic">
            Indicative experiences from our early Kaurs community.
          </p>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 md:py-20 bg-gray-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-10">Quick questions</h2>
          <div className="space-y-3">
            {faqs.map((f, i) => (
              <div
                key={i}
                className="border border-gray-200 rounded-lg overflow-hidden bg-white"
                data-testid={`angel-faq-item-${i}`}
              >
                <button
                  type="button"
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-gray-50 transition"
                >
                  <span className="font-semibold pr-4">{f.q}</span>
                  <ChevronDown
                    className={`w-5 h-5 flex-shrink-0 transition-transform ${openFaq === i ? 'rotate-180' : ''}`}
                  />
                </button>
                {openFaq === i && (
                  <div className="px-5 pb-4 text-gray-600 text-sm leading-relaxed">{f.a}</div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FORM */}
      <section ref={formRef} id="apply" className="py-16 md:py-20 bg-white scroll-mt-20">
        <div className="max-w-xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <h2 className="text-3xl md:text-4xl font-bold mb-3">Sign up as a Kaur</h2>
            <p className="text-gray-600">Free forever. Takes about 2 minutes.</p>
          </div>

          <div className="bg-white border border-gray-200 rounded-2xl shadow-lg p-8">
            {error && (
              <div
                className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center space-x-2 text-red-700"
                data-testid="angel-register-error"
              >
                <AlertCircle className="w-5 h-5" />
                <span className="text-sm">{error}</span>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-5" data-testid="angel-register-form">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                  Full Name
                </label>
                <input
                  id="name" name="name" type="text" required
                  value={formData.name} onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-600 focus:border-transparent"
                  placeholder="Your Name"
                  data-testid="angel-name-input"
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                  Email Address
                </label>
                <input
                  id="email" name="email" type="email" required
                  value={formData.email} onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-600 focus:border-transparent"
                  placeholder="you@example.com"
                  data-testid="angel-email-input"
                />
              </div>

              <div>
                <label htmlFor="mobile" className="block text-sm font-medium text-gray-700 mb-2">
                  Mobile Number
                </label>
                <input
                  id="mobile" name="mobile" type="tel"
                  value={formData.mobile} onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-600 focus:border-transparent"
                  placeholder="+91 99999 99999"
                  data-testid="angel-mobile-input"
                />
              </div>

              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                  Password
                </label>
                <input
                  id="password" name="password" type="password" required
                  value={formData.password} onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-600 focus:border-transparent"
                  placeholder="••••••••"
                  data-testid="angel-password-input"
                />
              </div>

              <div>
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-2">
                  Confirm Password
                </label>
                <input
                  id="confirmPassword" name="confirmPassword" type="password" required
                  value={formData.confirmPassword} onChange={handleChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-600 focus:border-transparent"
                  placeholder="••••••••"
                  data-testid="angel-confirm-password-input"
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-gradient-to-r from-purple-600 to-pink-600 text-white py-3 rounded-lg font-semibold hover:from-purple-700 hover:to-pink-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="angel-submit-button"
              >
                {loading ? 'Creating account...' : "Join as Nanaki's Kaur"}
              </button>
            </form>

            <div className="mt-6 text-center">
              <p className="text-sm text-gray-600">
                Already have an account?{' '}
                <Link to="/login" className="text-purple-600 font-semibold hover:underline">
                  Sign in
                </Link>
              </p>
            </div>
          </div>

          <p className="text-xs text-center text-gray-500 mt-6">
            By signing up you agree to our community guidelines. Zero joining fees, cancel anytime.
          </p>
        </div>
      </section>
    </div>
  );
};

export default RegisterAngel;
